import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Phase 106 — Magic-login exchange.
 *
 * Lands here from the WhatsApp / email magic link generated by the Phase 106
 * Lead → Customer auto-onboarding endpoint. Exchanges the token for a session
 * + drops the user onto /dashboard.
 */
export default function MagicLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser } = useAuth();
  const [status, setStatus] = useState('exchanging');     // exchanging | success | error
  const [error, setError] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setError('No magic-login token in the URL');
      setStatus('error');
      return;
    }
    (async () => {
      try {
        const res = await api.post('/auth/magic-login', { token });
        const session = res.data?.access_token || res.data?.token;
        const user = res.data?.user;
        if (!session || !user) throw new Error('Invalid response');
        // Mirror what AuthContext.login does — persist + set state
        localStorage.setItem('outplay_token', session);
        localStorage.setItem('outplay_user', JSON.stringify(user));
        setToken(session);
        setUser(user);
        setStatus('success');
        toast.success(`Welcome to Out-Play, ${user.name}!`);
        // Tiny delay so users see the success state animate
        setTimeout(() => navigate('/dashboard'), 1200);
      } catch (err) {
        setError(err.response?.data?.detail || 'Magic link is invalid or has expired');
        setStatus('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-emerald-50 p-6" data-testid="magic-login-page">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center space-y-4">
          {status === 'exchanging' && (
            <>
              <div className="mx-auto h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-teal-600 animate-spin" />
              </div>
              <h1 className="text-xl font-display font-semibold">Signing you in…</h1>
              <p className="text-sm text-muted-foreground">Hold on a moment while we verify your magic link.</p>
              <Skeleton className="h-2 w-3/4 mx-auto" />
            </>
          )}
          {status === 'success' && (
            <>
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-display font-semibold text-emerald-700">You're in!</h1>
              <p className="text-sm text-muted-foreground">Redirecting to your dashboard…</p>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="mx-auto h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-rose-600" />
              </div>
              <h1 className="text-xl font-display font-semibold text-rose-700">Link couldn't be used</h1>
              <p className="text-sm text-muted-foreground" data-testid="magic-login-error">{error}</p>
              <div className="pt-2 flex gap-2 justify-center">
                <Button variant="outline" onClick={() => navigate('/')}>Go home</Button>
                <Button onClick={() => navigate('/login')}>Sign in with password</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
